import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type OauthClientCreateInput,
  type OauthClientUpdateInput,
  oauthClientsApi,
} from '@/lib/api/oauth-clients';
import { oauthClientsQueryKeys } from '@/lib/query-keys/oauth-clients';

export const useOauthClientsQuery = () =>
  useQuery({
    queryKey: oauthClientsQueryKeys.list(),
    queryFn: () => oauthClientsApi.list(),
  });

export const getOauthClientQueryOptions = (id: string) => ({
  queryKey: oauthClientsQueryKeys.detail(id),
  queryFn: () => oauthClientsApi.get(id),
});

export const useOauthClientQuery = (id: string | null) =>
  useQuery({
    queryKey: id !== null ? oauthClientsQueryKeys.detail(id) : oauthClientsQueryKeys.all(),
    queryFn: id !== null ? () => oauthClientsApi.get(id) : skipToken,
  });

export const useCreateOauthClientMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: OauthClientCreateInput) => oauthClientsApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientsQueryKeys.list() }),
  });
};

export const useUpdateOauthClientMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: OauthClientUpdateInput }) =>
      oauthClientsApi.update(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: oauthClientsQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: oauthClientsQueryKeys.detail(id) });
    },
  });
};

export const useDeleteOauthClientMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => oauthClientsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientsQueryKeys.list() }),
  });
};

export const useRotateOauthClientSecretMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => oauthClientsApi.rotateSecret(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: oauthClientsQueryKeys.detail(id) });
    },
  });
};
